import { observer } from 'mobx-react-lite'
import { type ProfileViewModel } from '@/features/iam/users/ProfileViewModel'
import { TextInputContainer } from '@/components/inputs/TextInputContainer'
import { type ProfileGeneralViewModel } from '@/features/iam/users/ProfileGeneralViewModel'
import { type MemberMetadataField } from '@/features/iam/workspaces/Workspace'
import { isNotNil } from '@/utils/object-util'
import {
  MetadataInputField,
  UserInfo,
} from '@/features/iam/users/components/UserInfo'

export interface UserInfoContainerProps {
  vm: ProfileViewModel
}

export const UserInfoContainer = observer(function UserInfoContainer({
  vm,
}: UserInfoContainerProps) {
  return (
    <UserInfo
      onSubmit={vm.general.saveProfile}
      givenNameInput={
        <TextInputContainer
          model={vm.general}
          error={vm.general.validation.getError('givenName')}
          field="givenName"
          label="First Name"
          placeholder="First Name"
          isDisabled={!vm.general.canEdit}
        />
      }
      familyNameInput={
        <TextInputContainer
          model={vm.general}
          error={vm.general.validation.getError('familyName')}
          field="familyName"
          label="Last Name"
          placeholder="Last Name"
          isDisabled={!vm.general.canEdit}
        />
      }
      emailInput={
        <TextInputContainer
          model={vm.general}
          error={vm.general.validation.getError('email')}
          field="email"
          type="email"
          label="Email Address"
          placeholder="Email Address"
          isDisabled={!vm.general.canEdit}
        />
      }
      phoneInput={
        <TextInputContainer
          model={vm.general}
          error={vm.general.validation.getError('phone')}
          type="tel"
          field="phone"
          label="Phone"
          placeholder="Phone number"
          isDisabled={!vm.general.canEdit}
        />
      }
      metadataFields={vm.general.inputFields.map((field) => ({
        id: field.id,
        title: field.title,
        input: <MetadataInputFieldContainer field={field} vm={vm.general} />,
      }))}
      isSavePending={vm.general.saveProfile.pending}
      canSave={vm.general.canSave}
    />
  )
})

interface MetadataInputFieldContainerProps {
  field: MemberMetadataField
  vm: ProfileGeneralViewModel
}

const MetadataInputFieldContainer = observer(
  function MetadataInputFieldContainer({
    field,
    vm,
  }: MetadataInputFieldContainerProps) {
    const value =
      isNotNil(vm.memberMetadata[field.id]) && vm.memberMetadata[field.id]
    return (
      <MetadataInputField
        type={field.type}
        description={field.description}
        isDisabled={!field.permissions.includes('USER_WRITE')}
        value={value}
        onChange={(v) => vm.onMetadataChange(field.id, v)}
      />
    )
  },
)
